import React, { useState, useEffect } from 'react';
import { useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { Helmet } from 'react-helmet-async';
import { supabase } from '../lib/supabase';
import { Search, ChevronLeft, ChevronRight, Download, Mail, Filter, X, ChevronDown } from 'lucide-react';
import type { Result, Event, ColumnConfig } from '../types';
import ResultModal from './ResultModal';
import Logo from './Logo';
import { Link } from 'react-router-dom';

export default function SharedResults() {
  const { eventId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [event, setEvent] = useState<Event | null>(null);
  const [results, setResults] = useState<Result[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedResult, setSelectedResult] = useState<Result | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalResults, setTotalResults] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    gender: '',
    category: ''
  });
  const [genderDropdownState, setGenderDropdownState] = useState({ show: false, rect: null as DOMRect | null });
  const [categoryDropdownState, setCategoryDropdownState] = useState({ show: false, rect: null as DOMRect | null });
  const [availableFilters, setAvailableFilters] = useState<{
    genders: string[];
    categories: string[];
  }>({
    genders: [],
    categories: []
  });
  const [columnConfig, setColumnConfig] = useState<ColumnConfig[]>([]);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const RESULTS_PER_PAGE = 20;

  const genderButtonRef = React.useRef<HTMLButtonElement>(null);
  const categoryButtonRef = React.useRef<HTMLButtonElement>(null);

  // Add refs for the dropdown containers
  const genderDropdownRef = React.useRef<HTMLDivElement>(null);
  const categoryDropdownRef = React.useRef<HTMLDivElement>(null);

  const searchInputRef = React.useRef<HTMLInputElement>(null);

  const fromGroup = location.state?.fromGroup;

  useEffect(() => {
    loadEventAndResults();
  }, [eventId]);

  const loadResults = useCallback(async () => {
    if (!event?.id) return;
    
    console.log("Loading results for event:", event.id);
    console.log("Current filters:", filters);
    setLoadingMore(true);

    try {
      let query = supabase
        .from('results')
        .select('*', { count: 'exact' })
        .eq('event_id', event.id);

      if (filters.gender) {
        console.log("Applying gender filter:", filters.gender);
        query = query.eq('gender', filters.gender);
      }
      if (filters.category) {
        console.log("Applying category filter:", filters.category);
        query = query.eq('category', filters.category);
      }

if (searchTerm) {
  // Check if exact bib search is enabled for list mode
  if (event.list_mode_enabled && event.list_mode_exact_bib_search) {
    // Check if search term is numeric (potential bib number)
    const isNumeric = /^\d+$/.test(searchTerm);
    
    if (isNumeric) {
      // Only search by exact bib number
      query = query.eq('bib_number', searchTerm);
    } else {
      // Non-numeric search: search name, club, and custom fields
      query = query.or(
        `name.ilike.%${searchTerm}%,` +
        `club.ilike.%${searchTerm}%,` +
        `custom_1.ilike.%${searchTerm}%,` +
        `custom_2.ilike.%${searchTerm}%`
      );
    }
  } else {
    // Normal fuzzy search for everything
    query = query.or(
      `bib_number.ilike.%${searchTerm}%,` +
      `name.ilike.%${searchTerm}%,` +
      `club.ilike.%${searchTerm}%,` +
      `custom_1.ilike.%${searchTerm}%,` +
      `custom_2.ilike.%${searchTerm}%`
    );
  }
}

      const start = (currentPage - 1) * RESULTS_PER_PAGE;
      query = query
        .order('rank', { ascending: true, nullsFirst: false })
        .order('upload_order_index', { ascending: true, nullsFirst: false })
        .order('created_at', { ascending: true })
        .range(start, start + RESULTS_PER_PAGE - 1);

      const { data: resultsData, count, error: resultsError } = await query;

      if (resultsError) throw resultsError;
      
      setResults(resultsData || []);
      console.log("Results loaded:", resultsData?.length || 0, "with filters:", filters);
      setTotalResults(count || 0);
      
      // Mark initial load as complete only after first successful results load
      if (!initialLoadComplete) {
        setInitialLoadComplete(true);
      }
    } catch (err: any) {
      console.error('Error loading results:', err);
      setError(err.message || 'Failed to load results');
    } finally {
      setLoadingMore(false);
    }
  }, [event?.id, filters, searchTerm, currentPage]);

  useEffect(() => {
    if (event?.id) {
      loadResults();
      loadAvailableFilters();
    }
  }, [event?.id, loadResults]);

  const loadAvailableFilters = async () => {
    if (!event?.id) return;

    try {
      console.log("Loading filters for event:", event.id);
      
      // Use database aggregation to get distinct categories
      const { data: categoryData, error: categoryError } = await supabase.rpc(
        'get_distinct_categories_for_event',
        { p_event_id: event.id }
      );
      
      if (categoryError) {
        console.error('Error fetching categories:', categoryError);
        // Fallback to direct query if RPC fails
        const { data: fallbackCategoryData, error: fallbackError } = await supabase
          .from('results')
          .select('category')
          .eq('event_id', event.id)
          .not('category', 'is', null)
          .neq('category', '');
          
        if (fallbackError) throw fallbackError;
        
        const uniqueCategories = Array.from(new Set(
          fallbackCategoryData
            .map(r => r.category)
            .filter(c => c !== null && c !== undefined && c.trim() !== '')
        )).sort((a, b) => String(a).localeCompare(String(b)));
        
        // Use database aggregation to get distinct genders
        const { data: genderData, error: genderError } = await supabase
          .from('results')
          .select('gender')
          .eq('event_id', event.id)
          .not('gender', 'is', null);
          
        if (genderError) throw genderError;
        
        // Filter out null values
        const uniqueGenders = Array.from(new Set(genderData
          .map(r => r.gender)
          .filter(g => g !== null && g !== undefined && g.trim() !== '')
        )).sort((a, b) => String(a).localeCompare(String(b)));
        
        setAvailableFilters({
          genders: uniqueGenders,
          categories: uniqueCategories
        });
        
        return;
      }
      
      // Use database aggregation to get distinct genders
      const { data: genderData, error: genderError } = await supabase.rpc(
        'get_distinct_genders_for_event',
        { p_event_id: event.id }
      );
      
      if (genderError) throw genderError;

      setAvailableFilters({
        categories: (categoryData || []).filter(c => c !== null && c !== undefined && c.trim() !== ''),
        genders: (genderData || []).filter(g => g !== null && g !== undefined && g.trim() !== '')
      });
    } catch (err) {
      console.error('Error loading filters:', err);
    }
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (genderDropdownState.show && 
          genderDropdownRef.current && 
          !genderDropdownRef.current.contains(event.target as Node) &&
          !genderButtonRef.current?.contains(event.target as Node)) {
        setGenderDropdownState(prev => ({ ...prev, show: false }));
      }
      if (categoryDropdownState.show && 
          categoryDropdownRef.current && 
          !categoryDropdownRef.current.contains(event.target as Node) &&
          !categoryButtonRef.current?.contains(event.target as Node)) {
        setCategoryDropdownState(prev => ({ ...prev, show: false }));
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [genderDropdownState.show, categoryDropdownState.show]);

  // Update dropdown position on scroll
  useEffect(() => {
    const handleScroll = () => {
      if (genderDropdownState.show && genderButtonRef.current) {
        const rect = genderButtonRef.current.getBoundingClientRect();
        setGenderDropdownState(prev => ({ ...prev, rect }));
      }
      if (categoryDropdownState.show && categoryButtonRef.current) {
        const rect = categoryButtonRef.current.getBoundingClientRect();
        setCategoryDropdownState(prev => ({ ...prev, rect }));
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [genderDropdownState.show, categoryDropdownState.show]);

  // Update dropdown position when button is clicked
  const updateDropdownPosition = (buttonRef: React.RefObject<HTMLButtonElement>, setDropdownState: React.Dispatch<React.SetStateAction<{show: boolean, rect: DOMRect | null}>>) => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownState({ show: true, rect });
    }
  };

  const loadEventAndResults = async () => {
    if (!eventId) {
      setError('No event ID provided');
      setLoading(false);
      return;
    }

    try {
      console.log("Loading event with ID or slug:", eventId);
      // First try to load by ID
      let eventQuery = supabase
        .from('events')
        .select('*, column_config');
      
      // Check if eventId is a UUID or a custom slug
      const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(eventId);
      
      if (isUuid) {
        eventQuery = eventQuery.eq('id', eventId);
      } else {
        eventQuery = eventQuery.eq('custom_url_slug', eventId);
      }
      
      const { data: eventData, error: eventError } = await eventQuery.single();

      if (eventError) throw eventError;
      if (!eventData) throw new Error('Event not found');
      
      setEvent(eventData);
      console.log("Event loaded:", eventData);
      if (eventData.column_config) {
        setColumnConfig(eventData.column_config);
      }

      // Set loading to false after event is loaded - results loading will be handled separately
      setLoading(false);
    } catch (err: any) {
      console.error('Error loading event:', err);
      setError(err.message || 'Failed to load event');
      setLoading(false);
    }
  };

  const handleGenderSelect = (gender: string) => {
    console.log("Setting gender filter to:", gender);
    setFilters(prev => ({ ...prev, gender }));
    setCurrentPage(1);
    setGenderDropdownState(prev => ({ ...prev, show: false }));
  };

  const handleCategorySelect = (category: string) => {
    console.log("Setting category filter to:", category);
    setFilters(prev => ({ ...prev, category }));
    setCurrentPage(1);
    setCategoryDropdownState(prev => ({ ...prev, show: false }));
  };

  const downloadCsv = async () => {
    if (!event?.id) return;
    
    setLoading(true);
    try {
      const { data: fileList, error: listError } = await supabase.storage
        .from('original-results')
        .list(event.id);

      if (listError) throw listError;

      if (!fileList?.length) {
        throw new Error('No results file found');
      }

        const latestFile = fileList.sort((a, b) => {
          const timeA = new Date(a.updated_at || 0).getTime();
          const timeB = new Date(b.updated_at || 0).getTime();
          return timeB - timeA; // Newest first
        })[0];

        const { data, error: downloadError } = await supabase.storage
          .from('original-results')
          .download(`${event.id}/${latestFile.name}`);

      if (downloadError) throw downloadError;
      if (!data) throw new Error('Failed to download results file');

      const url = URL.createObjectURL(data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${event.name.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_results.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error('Error downloading results:', err);
      setError(err.message || 'Failed to download results. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleInquiry = () => {
    if (!event?.contact_email) return;

    const subject = encodeURIComponent(`Results Inquiry - ${event.name}`);
    const body = encodeURIComponent(
      `Hello,\n\nI would like to inquire about my results for ${event.name} on ${formatDate(event.date)}.\n\n`
    );
    
    window.location.href = `mailto:${event.contact_email}?subject=${subject}&body=${body}`;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const totalPages = Math.ceil(totalResults / RESULTS_PER_PAGE);

  const goToPage = (page: number) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  const clearFilters = () => {
    setFilters({
      gender: '',
      category: ''
    });
    setCurrentPage(1);
  };

  // Get visible columns based on configuration, ensuring at least the configured visible columns show
  const getVisibleColumns = (screenSize: 'mobile' | 'tablet' | 'desktop') => {
    const visibleColumns = columnConfig
      .filter(col => col.visible && col.type !== 'split_point');

    if (screenSize === 'mobile') {
      // On mobile, only show columns marked as visibleOnMobile
      return visibleColumns.filter(col => col.visibleOnMobile);
    } else if (screenSize === 'tablet') {
      // On tablet, show up to 6 columns
      return visibleColumns.slice(0, 6);
    } else {
      // On desktop, show all visible columns
      return visibleColumns;
    }
  };

  if (loading || !initialLoadComplete) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white p-8 rounded-lg shadow-lg text-center max-w-md">
          <div className="mb-6">
            <Logo className="h-8 mx-auto" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Results Not Found</h1>
          <p className="text-gray-600">
            {error || 'The requested event could not be found.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 relative flex flex-col">
      {event && (
        <Helmet>
          <title>{event.name} Results – {formatDate(event.date)} | Pacer Results</title>
          <meta name="description" content={`Official results for ${event.name} on ${formatDate(event.date)}. View finish times and rankings. Powered by Pacer Results.`} />
          <meta property="og:title" content={`${event.name} Results – ${formatDate(event.date)} | Pacer Results`} />
          <meta property="og:description" content={`Official results for ${event.name} on ${formatDate(event.date)}. View finish times and rankings. Powered by Pacer Results.`} />
          <meta property="og:image" content={`${window.location.origin}/meta/default-share.jpg`} />
          <meta property="og:url" content={window.location.href} />
          <link rel="canonical" href={window.location.href} />
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "SportsEvent",
              "name": event.name,
              "startDate": event.date,
              "eventStatus": "https://schema.org/EventScheduled",
              "eventAttendanceMode": "https://schema.org/OfflineEventAttendanceMode",
              "organizer": {
                "@type": "Organization",
                "name": "Pacer Results",
                "url": window.location.origin
              }
            })}
          </script>
        </Helmet>
      )}
      {event?.shared_results_background?.url && (
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url(${event.shared_results_background.url})`,
            opacity: event.shared_results_background.opacity
          }}
        />
      )}

      <div className="relative flex-1">
        <header className={`${event?.shared_results_background ? 'bg-white/90 backdrop-blur-sm' : 'bg-white'} shadow`}>
          <div className="container mx-auto px-4">
            <div className="flex justify-between items-center h-16">
              {event?.shared_results_logo?.url ? (
                <img 
                  src={event.shared_results_logo.url} 
                  alt={event.name}
                  style={{ height: event.shared_results_logo.height }}
                  className="max-w-[200px] object-contain"
                />
              ) : (
                <Logo className="h-5 fill-black" />
              )}

              {event?.timing_company_logo && (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-500">Timed by</span>
                  <img 
                    src={event.timing_company_logo.url} 
                    alt={event.timing_company_logo.name}
                    className="h-8 max-w-[120px] object-contain"
                  />
                </div>
              )}
            </div>
          </div>
        </header>

        <main className="container mx-auto px-4 py-8">
          <div className="space-y-6">
            <div className={`${event?.shared_results_background ? 'bg-white/90 backdrop-blur-sm p-6 rounded-lg shadow' : ''}`}>
              <div className="flex justify-between items-start">
                <div>
                  {fromGroup && event?.group_id && (
                    <button
                      onClick={() => navigate(`/results/group/${event.group_id}`)}
                      className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-2"
                    >
                      <ChevronLeft className="w-4 h-4" />
                      Back to All Events
                    </button>
                  )}
                  <h1 className="text-2xl font-bold text-gray-900">{event?.name}</h1>
                  <p className="text-gray-600">{event && formatDate(event.date)}</p>
                </div>
                <div className="flex items-center gap-2">
                  {event?.contact_email && (
                    <button
                      onClick={handleInquiry}
                      className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
                      title="Send results inquiry"
                    >
                      <Mail className="w-4 h-4" />
                      <span className="hidden sm:inline">Results Inquiry</span>
                    </button>
                  )}
                  {!event?.disable_results_download && (
                    <button
                      onClick={downloadCsv}
                      disabled={loading || loadingMore}
                      className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
                      title="Download results as CSV"
                    >
                      <Download className="w-4 h-4" />
                      <span className="hidden sm:inline">Download Results</span>
                    </button>
                  )}
                </div>
              </div>
            </div>
            
            <div className="flex gap-2">
              <div className="relative flex-1">
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Search by name, bib number and more..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                  onFocus={(e) => {
                    if (event?.list_mode_enabled && event?.list_mode_auto_select_search) {
                      e.target.select();
                    }
                  }}
                  onKeyDown={(e) => {
                    if (event?.list_mode_enabled && event?.list_mode_auto_select_search) {
                      if (e.key === 'Enter' || e.key === '\r') {
                        e.currentTarget.select();
                      }
                    }
                  }}
                  className={`block w-full px-3 py-2 border border-gray-300 rounded-md leading-5 ${
                    event?.shared_results_background
                      ? 'bg-white/90 backdrop-blur-sm placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-gray-500 focus:border-gray-500'
                      : 'bg-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-gray-500 focus:border-gray-500'
                  } sm:text-sm`}
                />
              </div>

              <div className="flex items-center gap-2">
              {!(event?.list_mode_enabled && event?.list_mode_hide_filters) && (
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className={`flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md cursor-pointer hover:bg-gray-100 transition-colors ${
                    showFilters || filters.gender || filters.category
                      ? 'bg-gray-900 text-white hover:bg-gray-800'
                      : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <Filter className="w-4 h-4" />
                  Filters
                  {(filters.gender || filters.category) && (
                    <span className="ml-1 px-2 py-0.5 text-xs bg-white text-gray-900 rounded-full">
                      {[filters.gender, filters.category].filter(Boolean).length}
                    </span>
                  )}
                </button>
              )}

                {(filters.gender || filters.category) && (
                  <button
                    onClick={clearFilters}
                    className="p-2 text-red-500 hover:text-red-700 cursor-pointer hover:bg-red-50 bg-red-100 rounded-full transition-colors border border-red-200"
                    title="Clear filters"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>

            {showFilters && (
              <div className={`${
                event?.shared_results_background 
                  ? 'bg-white/90 backdrop-blur-sm'
                  : 'bg-white'
              } p-4 rounded-lg border border-gray-200 grid grid-cols-1 sm:grid-cols-2 gap-4`}>
                <div className="gender-dropdown">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Gender
                  </label>
                  <div className="relative z-10">
                    <button
                      ref={genderButtonRef}
                      type="button"
                      onClick={() => updateDropdownPosition(genderButtonRef, setGenderDropdownState)}
                      className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md focus:ring-gray-500 focus:border-gray-500 cursor-pointer hover:bg-gray-50 transition-colors flex items-center justify-between"
                    >
                      <span>{filters.gender || 'All Genders'}</span>
                      <ChevronDown className="h-4 w-4 text-gray-500" />
                    </button>
                  </div>
                  
                  {genderDropdownState.show && genderDropdownState.rect && createPortal(
                    <div 
                      ref={genderDropdownRef}
                      className="fixed bg-white rounded-md shadow-lg border border-gray-200 py-1 max-h-60 overflow-y-auto z-[9999]"
                      style={{
                        top: `${genderDropdownState.rect.bottom}px`,
                        left: `${genderDropdownState.rect.left}px`,
                        width: `${genderDropdownState.rect.width}px`,
                        maxHeight: '300px'
                      }}
                    >
                      <button
                        type="button"
                        onClick={() => {
                          handleGenderSelect('');
                        }}
                        className={`w-full px-4 py-2 text-left hover:bg-gray-50 ${!filters.gender ? 'bg-gray-50 font-medium' : ''}`}
                      >
                        All Genders
                      </button>
                      {availableFilters.genders.map(gender => (
                        <button
                          key={gender}
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleGenderSelect(gender);
                          }}
                          className={`w-full px-4 py-2 text-left hover:bg-gray-50 ${
                            filters.gender === gender ? 'bg-gray-50 font-medium' : ''
                          }`}
                        >
                          {gender}
                        </button>
                      ))}
                    </div>,
                    document.body
                  )}
                </div>

                <div className="category-dropdown">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Category
                  </label>
                  <div className="relative z-10">
                    <button
                      ref={categoryButtonRef}
                      type="button"
                      onClick={() => updateDropdownPosition(categoryButtonRef, setCategoryDropdownState)}
                      className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md focus:ring-gray-500 focus:border-gray-500 cursor-pointer hover:bg-gray-50 transition-colors flex items-center justify-between"
                    >
                      <span>{filters.category || 'All Categories'}</span>
                      <ChevronDown className="h-4 w-4 text-gray-500" />
                    </button>
                  </div>
                  
                  {categoryDropdownState.show && categoryDropdownState.rect && createPortal(
                    <div 
                      ref={categoryDropdownRef}
                      className="fixed bg-white rounded-md shadow-lg border border-gray-200 py-1 max-h-60 overflow-y-auto z-[9999]"
                      style={{
                        top: `${categoryDropdownState.rect.bottom}px`,
                        left: `${categoryDropdownState.rect.left}px`,
                        width: `${categoryDropdownState.rect.width}px`,
                        maxHeight: '300px'
                      }}
                    >
                      <button
                        type="button"
                        onClick={() => {
                          handleCategorySelect('');
                        }}
                        className={`w-full px-4 py-2 text-left hover:bg-gray-50 ${!filters.category ? 'bg-gray-50 font-medium' : ''}`}
                      >
                        All Categories
                      </button>
                      {availableFilters.categories.map(category => (
                        <button
                          key={category}
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCategorySelect(category);
                          }}
                          className={`w-full px-4 py-2 text-left hover:bg-gray-50 ${
                            filters.category === category ? 'bg-gray-50 font-medium' : ''
                          }`}
                        >
                          {category}
                        </button>
                      ))}
                    </div>,
                    document.body
                  )}
                </div>
              </div>
            )}

            <div>
              <div className={`${
                event?.shared_results_background 
                  ? 'bg-white/90 backdrop-blur-sm relative'
                  : 'bg-white relative'
              } rounded-lg shadow overflow-hidden z-0`}>
                <div className="overflow-x-auto">
                  {/* Mobile View */}
                  <table className="min-w-full divide-y divide-gray-200 md:hidden">
                    <thead className="bg-gray-50">
                      <tr>
                        {getVisibleColumns('mobile').map(column => (
                          <th 
                            key={column.id}
                            className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                          >
                            {column.displayName}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {results.map((result) => (
                        <tr
                          key={result.id}
                          onClick={() => {
  if (!(event?.list_mode_enabled && event?.list_mode_disable_result_modal)) {
    setSelectedResult(result);
  }
}}
                          className={`hover:bg-gray-50 ${
  event?.list_mode_enabled && event?.list_mode_disable_result_modal 
    ? 'cursor-default' 
    : 'cursor-pointer'
}`}
                        >
                          {getVisibleColumns('mobile').map(column => (
                            <td 
                              key={column.id}
                              className="px-3 py-2 whitespace-nowrap text-xs text-gray-900"
                            >
                              {column.type === 'club' && result.club_position ? (
                                <span>
                                  {result[column.type]}
                                  <span className="text-gray-500 ml-1">
                                    (#{result.club_position})
                                  </span>
                                </span>
                              ) : (
                                column.type === 'custom_1' || column.type === 'custom_2'
                                  ? (result[column.type] || '')
                                  : result[column.type]
                              )}
                            </td>
                          ))}
                        </tr>
                      ))}
                      {results.length === 0 && !loadingMore && (
                        <tr>
                          <td colSpan={getVisibleColumns('mobile').length} className="px-3 py-4 text-center text-xs text-gray-500">
                            No results found
                          </td>
                        </tr>
                      )}
                      {loadingMore && (
                        <tr>
                          <td colSpan={getVisibleColumns('mobile').length} className="px-3 py-4 text-center text-xs text-gray-500">
                            <div className="flex justify-center">
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-900"></div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>

                  {/* Tablet View */}
                  <table className="min-w-full divide-y divide-gray-200 hidden md:table lg:hidden">
                    <thead className="bg-gray-50">
                      <tr>
                        {getVisibleColumns('tablet').map(column => (
                          <th 
                            key={column.id}
                            className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                          >
                            {column.displayName}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {results.map((result) => (
                        <tr
                          key={result.id}
                          onClick={() => {
  if (!(event?.list_mode_enabled && event?.list_mode_disable_result_modal)) {
    setSelectedResult(result);
  }
}}
                          className={`hover:bg-gray-50 ${
  event?.list_mode_enabled && event?.list_mode_disable_result_modal 
    ? 'cursor-default' 
    : 'cursor-pointer'
}`}
                        >
                          {getVisibleColumns('tablet').map(column => (
                            <td 
                              key={column.id}
                              className="px-4 py-3 whitespace-nowrap text-sm text-gray-900"
                            >
                              {column.type === 'club' && result.club_position ? (
                                <span>
                                  {result[column.type]}
                                  <span className="text-gray-500 ml-1">
                                    (#{result.club_position})
                                  </span>
                                </span>
                              ) : (
                                column.type === 'custom_1' || column.type === 'custom_2'
                                  ? (result[column.type] || '')
                                  : result[column.type]
                              )}
                            </td>
                          ))}
                        </tr>
                      ))}
                      {results.length === 0 && !loadingMore && (
                        <tr>
                          <td colSpan={getVisibleColumns('tablet').length} className="px-4 py-4 text-center text-sm text-gray-500">
                            No results found
                          </td>
                        </tr>
                      )}
                      {loadingMore && (
                        <tr>
                          <td colSpan={getVisibleColumns('tablet').length} className="px-4 py-4 text-center text-sm text-gray-500">
                            <div className="flex justify-center">
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-900"></div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>

                  {/* Desktop View */}
                  <table className="min-w-full divide-y divide-gray-200 hidden lg:table">
                    <thead className="bg-gray-50">
                      <tr>
                        {getVisibleColumns('desktop').map(column => (
                          <th 
                            key={column.id}
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                          >
                            {column.displayName}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {results.map((result) => (
                        <tr
                          key={result.id}
                          onClick={() => {
  if (!(event?.list_mode_enabled && event?.list_mode_disable_result_modal)) {
    setSelectedResult(result);
  }
}}
                          className={`hover:bg-gray-50 ${
  event?.list_mode_enabled && event?.list_mode_disable_result_modal 
    ? 'cursor-default' 
    : 'cursor-pointer'
}`}
                        >
                          {getVisibleColumns('desktop').map(column => (
                            <td 
                              key={column.id}
                              className="px-6 py-4 whitespace-nowrap text-sm text-gray-900"
                            >
                              {column.type === 'club' && result.club_position ? (
                                <span>
                                  {result[column.type]}
                                  <span className="text-gray-500 ml-1">
                                    (#{result.club_position})
                                  </span>
                                </span>
                              ) : (
                                column.type === 'custom_1' || column.type === 'custom_2'
                                  ? (result[column.type] || '')
                                  : result[column.type]
                              )}
                            </td>
                          ))}
                        </tr>
                      ))}
                      {results.length === 0 && !loadingMore && (
                        <tr>
                          <td colSpan={getVisibleColumns('desktop').length} className="px-6 py-4 text-center text-sm text-gray-500">
                            No results found
                          </td>
                        </tr>
                      )}
                      {loadingMore && (
                        <tr>
                          <td colSpan={getVisibleColumns('desktop').length} className="px-6 py-4 text-center text-sm text-gray-500">
                            <div className="flex justify-center">
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-900"></div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>

                </div>

                {totalPages > 1 && (
                  <div className="px-3 sm:px-6 py-3 flex items-center justify-between border-t border-gray-200 bg-gray-50">
                    <div className="flex-1 flex justify-between sm:hidden">
                      <button
                        onClick={() => goToPage(currentPage - 1)}
                        disabled={currentPage === 1 || loadingMore}
                        className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                      >
                        Previous
                      </button>
                      <button
                        onClick={() => goToPage(currentPage + 1)}
                        disabled={currentPage === totalPages || loadingMore}
                        className="relative ml-3 inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                      >
                        Next
                      </button>
                    </div>
                    <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                      <div>
                        <p className="text-sm text-gray-700">
                          Showing{' '}
                          <span className="font-medium">
                            {Math.min((currentPage - 1) * RESULTS_PER_PAGE + 1, totalResults)}
                          </span>{' '}
                          to{' '}
                          <span className="font-medium">
                            {Math.min(currentPage * RESULTS_PER_PAGE, totalResults)}
                          </span>{' '}
                          of <span className="font-medium">{totalResults}</span> results
                        </p>
                      </div>
                      <div>
                        <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                          <button
                            onClick={() => goToPage(currentPage - 1)}
                            disabled={currentPage === 1 || loadingMore}
                            className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                          >
                            <span className="sr-only">Previous</span>
                            <ChevronLeft className="h-5 w-5" />
                          </button>
                          
                          {[...Array(totalPages)].map((_, index) => {
                            const pageNumber = index + 1;
                            const isCurrentPage = pageNumber === currentPage;
                            
                            if (
                              pageNumber === 1 ||
                              pageNumber === totalPages ||
                              (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
                            ) {
                              return (
                                <button
                                  key={pageNumber}
                                  onClick={() => goToPage(pageNumber)}
                                  disabled={loadingMore}
                                  className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                    isCurrentPage
                                      ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                                      : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                                  } ${loadingMore ? 'opacity-50' : ''}`}
                                >
                                  {pageNumber}
                                </button>
                              );
                            }
                            
                            if (
                              pageNumber === currentPage - 2 ||
                              pageNumber === currentPage + 2
                            ) {
                              return (
                                <span
                                  key={pageNumber}
                                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700"
                                >
                                  ...
                                </span>
                              );
                            }
                            
                            return null;
                          })}

                          <button
                            onClick={() => goToPage(currentPage + 1)}
                            disabled={currentPage === totalPages || loadingMore}
                            className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                          >
                            <span className="sr-only">Next</span>
                            <ChevronRight className="h-5 w-5" />
                          </button>
                        </nav>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </main>
      </div>

      <footer className={`${
        event?.shared_results_background 
          ? 'bg-white/80 backdrop-blur-sm border-t border-gray-200/50'
          : 'bg-white border-t border-gray-200'
      } py-3 mt-auto`}>
        <div className="container mx-auto px-4">
          <div className="flex flex-col sm:flex-row justify-between items-center text-xs text-gray-500">
            <div>
              © {new Date().getFullYear()} Pacer Results
            </div>
            <div className="flex gap-4 mt-2 sm:mt-0">
              <Link to="/terms" className="hover:text-gray-700">Terms</Link>
              <Link to="/privacy" className="hover:text-gray-700">Privacy</Link>
            </div>
          </div>
        </div>
      </footer>

      {selectedResult && event && (
        <ResultModal
          result={selectedResult}
          event={event}
          onClose={() => setSelectedResult(null)}
        />
      )}
    </div>
  );
}